import { Navigate, useLocation } from 'react-router-dom';
import { useFirmManagedCase } from '@/hooks/useFirmManagedCase';
import { useExperiencePreset, useResolvedFeatures } from '@/hooks/useFeature';

/**
 * Registry-driven route gate (B2B Phase A, #1657). If the current route maps to
 * a registered feature that resolves OFF for this experience preset, redirect —
 * so hiding a nav item is backed by real route-level enforcement (direct URLs
 * included), not just visual hiding. Routes with no registered feature pass
 * through untouched (default visible).
 *
 * Applied globally in the app layout. Renders nothing while the preset signal
 * (firm-managed) is still resolving so a gated page never flashes.
 */
export function FeatureRouteGuard({ children }: { children: React.ReactNode }) {
  const { pathname } = useLocation();
  const { isLoading } = useFirmManagedCase();
  const { routeEnabled } = useResolvedFeatures();
  const preset = useExperiencePreset();

  if (isLoading) return null;
  // The collector product has one home: sign-in and legacy links that land on the dashboard go to /collection.
  if (preset === 'collector' && (pathname === '/dashboard' || pathname === '/')) return <Navigate to="/collection" replace />;
  if (!routeEnabled(pathname)) {
    // The collector product has one home: /collection (M1 #1747).
    return <Navigate to={preset === 'collector' ? '/collection' : '/dashboard'} replace />;
  }
  return <>{children}</>;
}
